/**
 * Which timestamps the batch monitor shows, and the run's duration.
 *
 * A finished round shows Inicio / Fin / Duración from `started_at` →
 * `completed_at`. It must NOT show `updated_at` then: that column also moves on
 * edits unrelated to processing (generating the Resumen IA, a rename), so a
 * 3.5 h round read as "Inicio 03:37 · Última actualización 12:56" and looked
 * like a 9 h run (COLBACH, 2026-09-29). `updated_at` is only meaningful while
 * the round is live, where the worker bumps it per expediente.
 */

import type { BatchStatus } from "../../api/client";

export type TimingField = {
  label: string;
  /** ISO timestamp, rendered by the screen. */
  iso?: string | null;
  /** Pre-formatted text (durations). */
  text?: string;
  placeholder: string;
};

/** "3 h 8 min", "12 min", "45 s" — coarse on purpose; the monitor isn't a stopwatch. */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const totalS = Math.floor(ms / 1000);
  if (totalS < 60) return `${totalS} s`;
  const totalMin = Math.floor(totalS / 60);
  const h = Math.floor(totalMin / 60);
  const min = totalMin % 60;
  if (h === 0) return `${min} min`;
  return min === 0 ? `${h} h` : `${h} h ${min} min`;
}

function durationBetween(fromIso: string | null | undefined, toMs: number): string | undefined {
  if (!fromIso) return undefined;
  const from = new Date(fromIso).getTime();
  if (Number.isNaN(from) || Number.isNaN(toMs)) return undefined;
  return formatDuration(toMs - from);
}

export function timingFields(status: BatchStatus, nowMs: number): TimingField[] {
  const inicio: TimingField = { label: "Inicio", iso: status.started_at, placeholder: "Sin iniciar" };
  const live = status.state === "queued" || status.state === "running";
  if (live) {
    return [
      inicio,
      {
        label: "Tiempo transcurrido",
        text: durationBetween(status.started_at, nowMs),
        placeholder: "—",
      },
      { label: "Última actividad", iso: status.updated_at, placeholder: "—" },
    ];
  }
  const end = status.completed_at ? new Date(status.completed_at).getTime() : NaN;
  return [
    inicio,
    { label: "Fin", iso: status.completed_at, placeholder: "No disponible" },
    {
      label: "Duración del procesamiento",
      text: durationBetween(status.started_at, end),
      placeholder: "—",
    },
  ];
}
